import React from 'react';
import { motion } from 'framer-motion';
import { LuxuryImageFrame } from '../ui/LuxuryImageFrame';
const poolNightAsset = { url: "/assets/pool-night.jpg" };
const poolFireworksAsset = { url: "/assets/pool-fireworks.jpg" };
const poolDayAsset = { url: "/assets/pool-day.jpg" };

const CornerOrnament = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 100 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg" 
    className={className}
  >
    <defs>
      <linearGradient id="goldGradientPool" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D4AF37" />
        <stop offset="50%" stopColor="#F9E498" />
        <stop offset="100%" stopColor="#D4AF37" />
      </linearGradient>
    </defs>
    <path 
      d="M95 5C80 5 60 10 40 25C20 40 5 65 5 95" 
      stroke="url(#goldGradientPool)" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path 
      d="M75 15C85 10 95 10 95 25C95 40 85 45 75 40C65 35 65 20 75 15Z" 
      fill="url(#goldGradientPool)" 
      fillOpacity="0.1"
      stroke="url(#goldGradientPool)" 
      strokeWidth="0.8"
    />
    <path 
      d="M25 75C10 85 10 95 25 95C40 95 45 85 40 75C35 65 20 65 25 75Z" 
      fill="url(#goldGradientPool)" 
      fillOpacity="0.1"
      stroke="url(#goldGradientPool)" 
      strokeWidth="0.8"
    />
    <circle cx="95" cy="5" r="2" fill="url(#goldGradientPool)" />
    <circle cx="5" cy="95" r="2" fill="url(#goldGradientPool)" />
  </svg>
);

const TextCard = ({ title, index }: { title: string, index: number }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
    className="relative -mt-2 sm:-mt-8 md:-mt-12 z-20 w-[96%] mx-auto"
  >
    <div className="relative group">
      <div className="absolute inset-0 bg-black/5 blur-lg -z-10 rounded-lg translate-y-1" />
      
      <div 
        className="relative bg-[#FDFCF8] px-1.5 py-2 sm:px-4 sm:py-6 md:px-6 md:py-8 border border-[#D4AF37]/30 shadow-sm rounded-lg overflow-hidden"
        style={{
          clipPath: "polygon(0% 8px, 8px 8px, 8px 0%, 16px 0%, 16px 8px, 84% 8px, 84% 0%, 92% 0%, 92% 8px, 100% 8px, 100% calc(100% - 8px), 92% calc(100% - 8px), 92% 100%, 84% 100%, 84% calc(100% - 8px), 16% calc(100% - 8px), 16% 100%, 8% 100%, 8% calc(100% - 8px), 0% calc(100% - 8px))"
        }}
      >
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/felt.png')] opacity-[0.03] pointer-events-none" />
        <div className="absolute inset-1 border-[0.5px] border-[#D4AF37]/20 rounded-md pointer-events-none" />
        
        <div className="relative z-10 text-center">
          <div className="flex justify-center mb-0.5 sm:mb-1">
            <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 rotate-45 border border-[#D4AF37]/40" />
          </div>
          
          <h3 className="font-serif text-[clamp(11px,3.2vw,24px)] !text-[#176B5B] italic tracking-wide leading-tight font-bold">
            {title}
          </h3>
        </div>
      </div>
    </div>
  </motion.div>
);


export const PoolSection = () => {
  const poolImages = [
    {
      url: poolNightAsset.url,
      alt: 'Walnut Farm Pool at Night',
      title: 'Time to Swim'
    },
    {
      url: poolFireworksAsset.url,
      alt: 'Poolside Fireworks Celebration',
      title: 'Soaking in the Sun'
    },
    {
      url: poolDayAsset.url,
      alt: 'Walnut Farm Pool Day View',
      title: "It's Pool Time"
    },
  ];

  return (
    <section className="bg-[#FDFCF8] py-12 sm:py-24 md:py-32 overflow-hidden border-t border-[#D4AF37]/10">
      <div className="w-full max-w-[1800px] mx-auto px-0.5 sm:px-4 md:px-8">
        <div className="text-center mb-8 sm:mb-16 md:mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="heading-luxury !text-[#176B5B] mb-2 sm:mb-4 font-bold"
          >
            Amazing Pool Area
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-clean text-base md:text-lg font-bold uppercase tracking-widest !text-[#B8863B]"
          >
            Keep swimming.
          </motion.p>
        </div>

        <div className="grid grid-cols-3 items-stretch justify-center gap-1 sm:gap-4 md:gap-6 lg:gap-8 w-full mx-auto px-0.5 sm:px-2">
          {poolImages.map((item, index) => (
            <div key={index} className="flex flex-col items-center w-full">
              <div className="w-full flex flex-col h-full">
                <LuxuryImageFrame index={index}>
                  <img
                    src={item.url}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                </LuxuryImageFrame>
                <TextCard 
                  title={item.title} 
                  index={index} 
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};